import React, {useState, useEffect, useCallback, useRef} from 'react';
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { useToasts } from 'react-toast-notifications';
import { ReactSortable } from "react-sortablejs";
import Avatar from './Avatar'
import PMedia from './PMedia';
import PMediaExisting from './PMediaExisting';
import {getRandomArbitrary} from '../utils/helper'
import useDidMountEffect from '../utils/useDidMountEffect';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { s3Link } from '../utils/constants'
import { startOfYesterday } from 'date-fns';
import moment from 'moment'

const UserStoryCreate = ({ show = false, handleAddStory, currentUser, getCreatorList = () => ({}) }) => {
    const { addToast } = useToasts();

    const [files, setFiles] = useState([])
    const [progressbar, setProgressbar] = useState({})
    const [fileError, setFileError] = useState({})
    const [done, setDone] = useState({})
    const [isSharing, setIsSharing] = useState(false)
    const [page, setPage] = useState(1)
    const [activeStory, setActiveStory] = useState({})
    const [hasMore, setHasMore] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [pageChange, setPageChange] = useState(0);
    const perPage = 5

    const observer = useRef();
    const lastElementRef = useCallback(node => {
        if (isLoading) return
        if (observer.current) {
            observer.current.disconnect()
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(page => page+1)
                setPageChange(pageChange => pageChange+1)
            }
        })

        if (node) observer.current.observe(node)
            return () => {
            observer.current.disconnect()
        }
    }, [isLoading, hasMore])

    const handleRemove = (file) => {
        console.log('file to remove: ', file)
        const index = files.findIndex(item => item.uuid == file.uuid)
        // cancel axios upload
        if (file.cancelTokenSource) {
            file.cancelTokenSource.cancel('File upload cancelled')
        }

        files.splice(index, 1)
        acceptedFiles.splice(index, 1)

        setFiles([...files])
    }

    useEffect(() => {
        getStories()
    }, [])

    const onDropRejected = useCallback(() => {
        setError('Only .jpg, .jpeg, .png, .mov, .mp4 files are accepted');
    }, []);

    const [stories, setStories] = useState([])
    const [isGettingStories, setIsGettingStories] = useState(false)

    const onDropAccepted = useCallback((acceptedFiles) => {
        let rank = files.length > 0 ? files.length - 1 : 0
        setActiveStory({})
        acceptedFiles.forEach((file) => {
            const uuid = uuidv4()
            const cancelTokenSource = axios.CancelToken.source();
            const fileObj = {
                uuid: uuid,
                file: file,
                rank: rank++,
                image: URL.createObjectURL(file),
                loaded: 0,
                isLibrary: false,
                cancelTokenSource: cancelTokenSource
            }
            setFiles([fileObj])
            /*
            const mediaIds = new Set(files.map(fileState => fileState.uuid));
            if (!mediaIds.has(fileObj.uuid)) {
                files.push(fileObj)
                setFiles([...files])
            }
            */
            uploadFile(fileObj)
        })
    }, [files]);

    const {
        acceptedFiles,
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        onDropRejected,
        onDropAccepted,
        accept: 'image/jpeg, image/png, video/mp4, video/quicktime',
        maxFiles: 1,
        multiple: false,
    });

    const uploadFile = (file) => {
        const formData = new FormData()
        formData.append('media', file.file, file.file.name)
        formData.append('uuid', file.uuid)
        formData.append('rank', file.uuid)
        formData.append('loaded', file.loaded)
        axios.post(`/api/medias`, formData, {
            cancelToken: file.cancelTokenSource.token,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: progressEvent => {
                const loaded = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 ))
                setProgressbar({
                    ...progressbar,
                    [file.uuid]: loaded > 85 ? getRandomArbitrary(85, 99) : loaded
                })
            },
        }).then(response => {
            setDone(response.data)
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('Request canceled', error.message);
                addToast(error.message, { appearance: 'warning', autoDismiss: true });
            } else {
                addToast(error.message, { appearance: 'error', autoDismiss: true });
            }
        })
    }

    useDidMountEffect(() => {
        const index = files.findIndex(item => item.uuid == done.uuid)
        const fileClones = [...files]
        if (index < 0) {
            fileClones.push({
                ...done,
                isLibrary: true,
                loaded: 100,
                uuid: done.uuid,
                rank: done.rank,
            })
        } else {
            fileClones[index] = {
                ...done,
                isLibrary: true,
                loaded: 100,
                uuid: done.uuid,
                rank: done.rank,
            }
        }
        
        setFiles([...fileClones])
    }, [done])

    const getStories = (source='onload') => {
        setIsGettingStories(true)
        axios.get('/api/stories', {
            params: {
                page,
                per_page: perPage
            }
        })
        .then(response => {
            console.log({response})
            setHasMore(response.data.data.length > 0)
            if(source == 'new') {
                setStories(response.data.data)
            } else {
                const storyIds = new Set(stories.map(story => story.id));
                const merged = [
                    ...stories,
                    ...response.data.data.filter(d => !storyIds.has(d.id))
                ];
                setStories(merged)
            }
        })
        .catch(err => {
            console.error(err); 
        })
        .finally(() => {
            setIsGettingStories(false)
        })
    }

    const updateThumbnailHandler = (file, thumb) => {
        console.log({file, thumb})
        const index = files.findIndex(item => item.uuid == file.uuid)
        const existingFiles = [...files]
        existingFiles[index].thumb_path = thumb.file_path
        setFiles([
            ...existingFiles
        ])
    }

    const handleShare = async () => {
        setIsSharing(true)
        await axios.post('/api/stories', {
            files: files
        }).then(response => {
            setPage(1)
            setPageChange(pageChange => pageChange+1)
            setDone({})
            handleClear()
            getCreatorList()
            setProgressbar(0)
            setFiles([])
            addToast('Story has been shared', { appearance: 'success', autoDismiss: true });
        })
        .catch(error => {
            addToast('Unable to share story', { appearance: 'error', autoDismiss: true });
        })
        .finally(() => {
            setIsSharing(false)
        })
    }

    const handleActiveStory = (story) => {
        console.log({ story })
        setActiveStory(story)
    }

    useEffect(() => {
        if (Object.keys(activeStory).length > 0) {
            activeStory.medias[0].uuid = uuidv4()
            activeStory.medias[0].isLibrary = true
            setFiles(activeStory.medias)
        }
    }, [activeStory])

    const handleUnshare = async () => {
        await axios.delete(`/api/stories/${activeStory.id}`)
            .then(response => {
                handleClear()
                getStories()
                addToast('Story has been removed', { appearance: 'success', autoDismiss: true });

                const index = files.findIndex(item => item.uuid == activeStory.uuid)

                files.splice(index, 1)
                acceptedFiles.splice(index, 1)
                setFiles([...files])

                getCreatorList()
            })
            .catch(error => {
                addToast('Unable to remove story', { appearance: 'error', autoDismiss: true });
            })
    }

    const handleClear = () => {
        setFiles([])
        setActiveStory({})
        setProgressbar(0)
    }

    useEffect(() => {
        if (page > 1) {
            getStories('loadMore')
        } else {
            getStories('new')
        }
    }, [pageChange])

    const videoOptions = {
        width: 240,
        height: 350,
        hideList: [
            'remainingTimeDisplay',
            'pictureInPictureToggle',
            'playbackRateMenuButton',
            'playToggle',
            'progressControl',
            'volumePanel'
        ]
    }

    const handleReShare = async () => {
        await axios.patch(`/api/stories/${activeStory.id}`)
            .then(response => {
                setPage(1)
                setPageChange(pageChange => pageChange+1)
                handleClear()
                addToast('Story has been shared', { appearance: 'success', autoDismiss: true });
                getCreatorList()
            })
            .catch(error => {
                addToast('Unable to share story', { appearance: 'error', autoDismiss: true });
            })
            .finally(() => {
                setIsSharing(false)
            })
    }

    return (
        <>
            <Modal className="add-story-modal" centered show={show} onHide={handleAddStory}>
                <Modal.Body>
                    <div className="add-story-row">
                        <div className="add-story-sidebar">
                            <div className="add-story-sidebar-header">
                                <button onClick={handleAddStory}>
                                    <FontAwesomeIcon icon={['fa', 'times']} />
                                </button>
                            </div>
                            <div className="add-story-sidebar-content">
                                <p>Stories</p>
                                <h2>Your Story</h2>
                                {/* <div className="add-story-sidebar-account">
                                    <Avatar
                                        avatar={
                                            currentUser.user
                                                ? currentUser.user.avatar_pic
                                                : ''
                                        }
                                        size="60"
                                    />
                                    <span className="mx-2">{currentUser.display_name}</span>
                                </div> */}
                                {/* List of stories here */}
                                <div className="stories mt-3">
                                    {
                                        stories.map((story, index) => {
                                            if (stories.length === index + 1) {
                                                return <div ref={lastElementRef} className={`story my-3${activeStory.id == story.id ? ' shadow-lg' : ''}`} onClick={() => handleActiveStory(story)} key={story.id}>
                                                    <div className="story__date">
                                                        <span className="story__month">
                                                            {moment.utc(story.created_at).local().format('MMM')}
                                                        </span>
                                                        <span className="story__day">
                                                            {moment.utc(story.created_at).local().format('DD')}
                                                        </span>
                                                    </div>
                                                    {
                                                        story.medias.length > 0 && <LazyLoadImage
                                                            src={`${s3Link}/${story.medias[0].thumb_path || story.medias[0].file_path}`}
                                                            className="rounded fluid shadow-sm"
                                                        />
                                                    }
                                                </div>
                                            } else {
                                                return <div className={`story my-3${activeStory.id == story.id ? ' shadow-lg' : ''}`} onClick={() => handleActiveStory(story)} key={story.id}>
                                                    <div className="story__date">
                                                        <span className="story__month">
                                                            {moment.utc(story.created_at).local().format('MMM')}
                                                        </span>
                                                        <span className="story__day">
                                                            {moment.utc(story.created_at).local().format('DD')}
                                                        </span>
                                                    </div>
                                                    {
                                                        story.medias.length > 0 && <LazyLoadImage
                                                            src={`${s3Link}/${story.medias[0].thumb_path || story.medias[0].file_path}`}
                                                            className="rounded fluid shadow-sm"
                                                        />
                                                    }
                                                </div>
                                            }
                                        })
                                    }
                                    
                                    {
                                        isGettingStories && <Spinner variant="primary" animation="border" size="sm" />
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="add-story-content">
                            <div className="add-story-options">
                                <div className="add-story-photo">
                                {
                                    files.map((file, index) => !!file.isLibrary
                                        ? <PMediaExisting
                                            file={file}
                                            index={index}
                                            removable={false}
                                            updateThumbnailHandler={updateThumbnailHandler}
                                            key={index}
                                            videoOptions={videoOptions}
                                            changeableCoverPic={false}
                                            width="240"
                                            height="350"
                                        />
                                        : <PMedia
                                                file={file}
                                                index={index}
                                                removable={false}
                                                progress={progressbar}
                                                key={index}
                                                height="350"
                                                width="240"
                                            />
                                    )
                                }
                                    <div {...getRootProps({ className: 'dropzone' })} style={{width: '100%'}}>
                                        <input {...getInputProps()} />
                                        {
                                            files.length <= 0
                                                ? <React.Fragment>
                                                    <div><FontAwesomeIcon icon={['far', 'image']} /></div>
                                                    <div>Create a story</div>
                                                </React.Fragment>
                                                : ''
                                        }
                                    </div>

                                </div>
                                <div className="my-3">
                                    {
                                        files.length > 0 && Object.keys(activeStory).length <= 0 && Object.keys(done).length <= 0 && <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={() => handleRemove(files[0])}
                                                className="mr-2"
                                            >
                                                Cancel
                                            </Button>
                                    }

                                    {
                                        files.length > 0 && Object.keys(activeStory).length <= 0 &&
                                            <Button
                                                type="button"
                                                variant="primary"
                                                onClick={handleShare}
                                                disabled={isSharing || Object.keys(done).length <= 0}
                                            >
                                                
                                                { isSharing ? 'Sharing...' : 'Share'}
                                            </Button>
                                    }
                                </div>
                                {
                                    files.length > 0 && Object.keys(activeStory).length > 0 && <div className="my-3">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleClear}
                                            className="mr-2"
                                        >
                                            Cancel
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="danger"
                                            onClick={handleUnshare}
                                            disabled={isSharing}
                                        >
                                            { isSharing ? 'Removing...' : 'Remove'}
                                        </Button>

                                        {
                                            moment(moment().utc().format()).diff(activeStory.created_at, 'hours') > 24 && <Button
                                                type="button"
                                                variant="primary"
                                                onClick={handleReShare}
                                                disabled={isSharing}
                                            >
                                                
                                                { isSharing ? 'Re-posting...' : 'Re-post'}
                                            </Button>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default UserStoryCreate;